import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Avatar,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AttachMoney as MoneyIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DynamicSEO from '../components/DynamicSEO';
import { generateProjectSEO } from '../lib/seoUtils';
import { getProjectById, ProjectWithDetails } from '../lib/api/projects';
import { hasProjectAccess } from '../lib/api/projectAccess';
import { completeProject } from '../lib/api/reviews';
import { supabase } from '../lib/supabaseClient';
import ProjectAccessModal from '../components/ProjectAccessModal';
import ProjectCompletionModal from '../components/ProjectCompletionModal';

const ProjectDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [project, setProject] = useState<ProjectWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [projectAccessModal, setProjectAccessModal] = useState<{
    open: boolean;
    projectId: string;
    projectTitle: string;
  }>({ open: false, projectId: '', projectTitle: '' });
  
  const [projectCompletionModal, setProjectCompletionModal] = useState(false);
  const [isProjectOwner, setIsProjectOwner] = useState(false);
  const [showCompletionSection, setShowCompletionSection] = useState(true);

  useEffect(() => {
    if (id) {
      loadProject();
    }
  }, [id]);

  const loadProject = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const projectData = await getProjectById(id!);
      setProject(projectData);
      
      // Check if user has access to project details (only if user is authenticated)
      try {
        const access = await hasProjectAccess(id!);
        setHasAccess(access);
        
        // Check if current user is the project owner
        const { data: { user } } = await supabase.auth.getUser();
        setIsProjectOwner(user?.id === projectData?.client_id);
      } catch (authError) {
        // User is not authenticated, show public view
        console.log('User not authenticated, showing public view');
        setHasAccess(false);
        setIsProjectOwner(false);
      }
    } catch (err: any) {
      console.error('Error loading project:', err);
      setError(err.message || 'Грешка при зареждане на проекта');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };


  const handleProjectAccessClick = () => {
    setProjectAccessModal({
      open: true,
      projectId: project!.id,
      projectTitle: project!.title
    });
  };

  const handleProjectAccessGranted = () => {
    // Reload project to get updated access data
    loadProject();
  };


  const handleCompleteProject = async () => {
    if (!id) return;
    
    try {
      const { success, error } = await completeProject(id);
      
      if (error) {
        console.error('Error completing project:', error);
        return;
      }
      
      if (success) {
        setProjectCompletionModal(true);
        // Reload project to get updated status
        loadProject();
      }
    } catch (err) {
      console.error('Error completing project:', err);
    }
  };

  const handleProjectCompleted = () => {
    setProjectCompletionModal(false);
  };

  const handleHideCompletionSection = () => {
    setShowCompletionSection(false);
  };

  // Removed handleViewProfile - clients can only view their own profile

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
    // TODO: Implement favorite functionality
  };

  const formatBudget = (min: number | null, max: number | null) => {
    if (!min && !max) return 'Договорна';
    if (!min) return `до €${max?.toLocaleString()}`;
    if (!max) return `от €${min.toLocaleString()}`;
    return `€${min.toLocaleString()} - €${max.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('bg-BG', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Току-що';
    if (diffInHours < 24) return `преди ${diffInHours}ч`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `преди ${diffInDays}д`;
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: '#28A0F0' }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || t('projectDetails.projectNotFound')}
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
              color: '#ffffff',
            }}
          >
            Назад
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: '#0f0f23', 
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {project && (
        <DynamicSEO 
          seoData={generateProjectSEO(project, i18n.language)} 
        />
      )}
      <Header />
      
      <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3, color: '#a0a0a0' }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/')}
            sx={{ color: '#28A0F0', textDecoration: 'none' }}
          >
            {t('projectDetails.backToHome')}
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/find-work')}
            sx={{ color: '#28A0F0', textDecoration: 'none' }}
          >
            {t('projectDetails.backToProjects')}
          </Link>
          <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
            {project.title}
          </Typography>
        </Breadcrumbs>

        {/* Back Button */}
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            mb: 3,
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: '#ffffff',
            '&:hover': {
              borderColor: '#28A0F0',
              backgroundColor: 'rgba(40, 160, 240, 0.1)',
            },
          }}
        >
          Назад
        </Button>

        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} lg={8}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: 3,
                overflow: 'hidden',
                mb: 3,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700, mb: 2 }}>
                      {project.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Chip
                        label={project.category?.name || 'Друго'}
                        sx={{
                          backgroundColor: 'rgba(40, 160, 240, 0.15)',
                          color: '#28A0F0',
                          border: '1px solid rgba(40, 160, 240, 0.3)',
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VisibilityIcon sx={{ color: '#a0a0a0', fontSize: '1rem' }} />
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {project.views_count} {t('projectDetails.views')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Tooltip title={isFavorite ? 'Премахни от любими' : 'Добави в любими'}>
                    <IconButton
                      onClick={toggleFavorite}
                      sx={{ color: isFavorite ? '#ef4444' : '#a0a0a0' }}
                    >
                      {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Description */}
                <Typography variant="body1" sx={{ color: '#d0d0d0', lineHeight: 1.7, mb: 4 }}>
                  {hasAccess ? (
                    project.description
                  ) : (
                    <>
                      {project.description.length > 200 
                        ? `${project.description.substring(0, 200)}...` 
                        : project.description}
                      <Box sx={{ 
                        mt: 3, 
                        p: 3, 
                        background: 'rgba(255, 87, 34, 0.1)', 
                        borderRadius: 2,
                        border: '1px solid rgba(255, 87, 34, 0.3)',
                        textAlign: 'center'
                      }}>
                        <Typography variant="h6" sx={{ color: '#ff5722', fontWeight: 600, mb: 2 }}>
                          🔒 За да видите пълното описание и контактите
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 3 }}>
                          Закупуване на достъп ще ви даде възможност да видите пълното описание на проекта, 
                          контактните данни на клиента и да кандидатствате за проекта.
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={handleProjectAccessClick}
                          sx={{
                            background: 'linear-gradient(135deg, #ff5722 0%, #d32f2f 100%)',
                            color: '#ffffff',
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            '&:hover': {
                              background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                            }
                          }}
                        >
                          Закупи достъп за 3 кредита
                        </Button>
                      </Box>
                    </>
                  )}
                </Typography>

                {/* Skills */}
                {project.skills && project.skills.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                      {t('projectDetails.requiredSkills')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {project.skills.map((skill, index) => (
                        <Chip
                          key={index}
                          label={skill}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: '#ffffff',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Project Details */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                    {t('projectDetails.projectDetails')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <MoneyIcon sx={{ color: '#28A0F0', fontSize: '1.2rem' }} />
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {t('projectDetails.budget')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#28A0F0', fontWeight: 600 }}>
                          {formatBudget(project.budget_min, project.budget_max)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TimeIcon sx={{ color: '#28A0F0', fontSize: '1.2rem' }} />
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {t('projectDetails.deadline')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                          {project.deadline ? formatDate(project.deadline) : t('projectDetails.flexible')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarIcon sx={{ color: '#28A0F0', fontSize: '1.2rem' }} />
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {t('projectDetails.published')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                          {getTimeAgo(project.created_at)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CheckCircleIcon sx={{ color: '#28A0F0', fontSize: '1.2rem' }} />
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {t('projectDetails.status')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600 }}>
                          {t('projectDetails.active')}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            {/* Client Info */}
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: 3,
                overflow: 'hidden',
                mb: 3,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 3 }}>
                  {t('projectDetails.forClient')}
                </Typography>
                
                {hasAccess ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar
                        src={project.client.avatar_url || undefined}
                        sx={{ width: 60, height: 60, bgcolor: '#28A0F0' }}
                      >
                        <PersonIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                          {project.client.full_name || project.client.company || t('findWork.anonymous')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {project.client.company ? t('findWork.company') : t('findWork.client')}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Contact Information */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                        Контактна информация
                      </Typography>
                      
                      <Stack spacing={2}>
                        
                        {project.client.company && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: '#a0a0a0', minWidth: '60px' }}>
                              Компания:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#ffffff' }}>
                              {project.client.company}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ 
                    p: 3, 
                    background: 'rgba(255, 87, 34, 0.1)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255, 87, 34, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="body1" sx={{ color: '#ff5722', fontWeight: 600, mb: 2 }}>
                      🔒 Контактната информация е скрита
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 3 }}>
                      За да видите контактите на клиента и да кандидатствате за проекта, 
                      трябва да закупите достъп.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleProjectAccessClick}
                      sx={{
                        background: 'linear-gradient(135deg, #ff5722 0%, #d32f2f 100%)',
                        color: '#ffffff',
                        px: 3,
                        py: 1,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                        }
                      }}
                    >
                      Закупи достъп за 3 кредита
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Complete Project Button - Only for project owner */}
            {isProjectOwner && showCompletionSection && (
              <Card sx={{ 
                background: 'rgba(255, 255, 255, 0.05)', 
                border: '1px solid rgba(255, 255, 255, 0.1)',
                mb: 3,
                position: 'relative'
              }}>
                <CardContent>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                      🎉 Проектът е готов?
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 3 }}>
                      Маркирайте проекта като завършен, за да можете да оставите ревю на изпълнителите, 
                      които са работили по него.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleCompleteProject}
                      disabled={project?.status === 'CLOSED'}
                      sx={{
                        background: project?.status === 'CLOSED' 
                          ? 'rgba(76, 175, 80, 0.3)'
                          : 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                        color: '#ffffff',
                        px: 3,
                        py: 1,
                        '&:hover': {
                          background: project?.status === 'CLOSED' 
                            ? 'rgba(76, 175, 80, 0.3)'
                            : 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                        },
                        '&:disabled': {
                          background: 'rgba(76, 175, 80, 0.3)',
                          color: 'rgba(255, 255, 255, 0.5)',
                        },
                      }}
                    >
                      {project?.status === 'CLOSED' ? 'Проектът е завършен' : 'Маркирай като завършен'}
                    </Button>
                  </Box>
                  
                  {/* Hide button */}
                  <IconButton
                    onClick={handleHideCompletionSection}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: '#a0a0a0',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                      },
                    }}
                    size="small"
                  >
                    <CancelIcon />
                  </IconButton>
                </CardContent>
              </Card>
            )}


          </Grid>
        </Grid>
      </Container>

      <Footer />

      {/* Project Access Modal */}
      <ProjectAccessModal
        open={projectAccessModal.open}
        onClose={() => setProjectAccessModal({ open: false, projectId: '', projectTitle: '' })}
        projectId={projectAccessModal.projectId}
        projectTitle={projectAccessModal.projectTitle}
        onAccessGranted={handleProjectAccessGranted}
      />

      {/* Project Completion Modal */}
      <ProjectCompletionModal
        open={projectCompletionModal}
        onClose={() => setProjectCompletionModal(false)}
        onProjectCompleted={handleProjectCompleted}
        projectId={project?.id || ''}
        projectTitle={project?.title || ''}
      />
    </Box>
  );
};

export default ProjectDetailsPage;
